<template>
  <div class="detail-card" data-card-id="vendor-ai">
    <div
      class="card-header"
      :class="{ 'slidein-header': mode === 'draggable' }"
    >
      <div class="header-left">
        <h5 class="card-title">
          Vendor AI Detections <span class="subtitle">(per image)</span>
        </h5>
      </div>
      <div class="header-right">
        <button
          class="drag-toggle-btn"
          @click.prevent="$emit('toggleDraggable')"
          :title="mode === 'draggable' ? 'Dock to panel' : 'Make draggable'"
        >
          <font-awesome-icon
            :icon="mode === 'draggable' ? ['fas', 'times'] : ['fas', 'clone']"
          />
        </button>
      </div>
    </div>
    <div class="card-content">
      <div class="detail-item">
        <span class="detail-label">Created on:</span>
        <span class="detail-value">{{
          formatDateTime(
            submission?.updated_at || submission?.created_at,
            "yyyy-MM-dd HH:mm",
          )
        }}</span>
      </div>
      <div class="detail-item">
        <span class="detail-label">Status:</span>
        <span class="detail-value">{{ submission?.status || "N/A" }}</span>
      </div>
      <div class="expandable-section">
        <a
          href="#"
          @click.prevent="aiAnalysisOpen = !aiAnalysisOpen"
          class="section-toggle"
        >
          <font-awesome-icon
            :icon="
              aiAnalysisOpen
                ? ['fas', 'chevron-down']
                : ['fas', 'chevron-right']
            "
            class="me-2"
          />
          <b>Details</b>
        </a>
        <div v-if="aiAnalysisOpen" class="pathology-list">
          <div
            v-for="pathology in aiPathologies.filter(
              (p) =>
                p.ai.predicted_diagnosis !== 'Cardio' &&
                p.ai.predicted_diagnosis !== 'Thoracic',
            )"
            :key="pathology.ai._id"
            class="pathology-item"
          >
            <div class="pathology-icon">
              <font-awesome-icon
                :icon="
                  pathology.feedback
                    ? ['fas', 'circle']
                    : ['fas', 'times-circle']
                "
                :style="{
                  color: pathology.project
                    ? pathology.project.color
                    : pathology.ai.colour,
                }"
              />
            </div>
            <div
              class="pathology-confidence"
              v-if="project?.display_confidence"
            >
              {{ (pathology.ai.confidence * 100).toFixed(2) }}%
            </div>
            <div class="pathology-name">
              {{
                pathology.project
                  ? pathology.project.label
                  : pathology.ai.predicted_diagnosis
              }}
              <small
                v-if="pathology.ai.location && pathology.ai.location.length > 0"
              >
                ({{ pathology.ai.location.join(", ") }})
              </small>
              <span v-if="pathology.feedback === false" class="removed-badge">
                (Removed by Moderator Feedback)
              </span>
              <span
                v-else-if="pathology.hasFeedback"
                class="confirmed-badge"
              >
                (Confirmed by Moderator)
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { formatDateTime } from "@/helpers/project-management/formatters";

const props = defineProps({
  submission: Object,
  aiPathologies: Array,
  project: Object,
  mode: {
    type: String,
    default: "parked",
  },
});

defineEmits(["toggleDraggable"]);

const aiAnalysisOpen = ref(false);
</script>

<style scoped>
/* Pathology List (migrated from global styles.css) */
.pathology-list {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0rem;
}

.pathology-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  background-color: var(--color-bg-primary);
  border-radius: var(--border-radius-small);
}

.pathology-icon {
  font-size: 1rem;
  flex-shrink: 0;
}

.pathology-confidence {
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-semibold);
  min-width: 50px;
  flex-shrink: 0;
}

.pathology-name {
  flex: 1;
  color: var(--color-text-primary);
  font-size: 0.9rem;
}

.pathology-name small {
  color: var(--color-text-secondary);
  font-size: 0.85rem;
}

.removed-badge {
  color: var(--color-accent-primary);
  font-size: 0.8rem;
  font-style: italic;
}

.confirmed-badge {
  color: var(--color-success, #28a745);
  font-size: 0.8rem;
  font-style: italic;
}
</style>
